import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal, Button, Form, Badge } from "react-bootstrap";
import { FiPlus, FiEdit2, FiTrash2, FiTruck } from "react-icons/fi";
import ConfirmModal from "../components/ConfirmModal";
import Pagination from "../components/Pagination";
import api from "../services/api";

// Format plat nomor Indonesia: B 1234 ABC
const formatPlate = (value) => {
  if (!value) return value;
  const raw = value.toUpperCase().replace(/\s+/g, "");
  const match = raw.match(/^([A-Z]{1,2})(\d{1,4})([A-Z]{0,3})$/);
  if (match) return [match[1], match[2], match[3]].filter(Boolean).join(" ");
  return raw;
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editVehicle, setEditVehicle] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [vehicleForm, setVehicleForm] = useState({
    vehicle_type: "",
    plate_number: "",
    brand: "",
    color: "",
  });
  const [loyaltyRules, setLoyaltyRules] = useState([]);
  const [loyaltyProgressEdit, setLoyaltyProgressEdit] = useState({});
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ show: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, show: false, onConfirm: null }));
  };

  useEffect(() => {
    loadCustomers();
    loadVehicleTypes();
    loadLoyaltyRules();
  }, []);

  const loadVehicleTypes = async () => {
    try {
      const res = await api.get("/settings/vehicle-types");
      setVehicleTypes(res.data.filter((vt) => vt.is_active));
    } catch (error) {
      /* ignore */
    }
  };

  const loadLoyaltyRules = async () => {
    try {
      const res = await api.get("/settings/loyalty-rules");
      setLoyaltyRules(res.data.filter((r) => r.is_active));
    } catch (error) {
      /* ignore */
    }
  };

  const loadCustomers = async (targetPage = page) => {
    try {
      const res = await api.get("/customers", {
        params: { search, page: targetPage, limit: PAGE_SIZE },
      });
      const data = res.data;
      // Backend lama bisa mengembalikan array polos
      if (Array.isArray(data)) {
        setCustomers(data);
        setTotalCount(data.length);
      } else {
        setCustomers(data.customers || []);
        setTotalCount(data.total || 0);
        // Backend menjepit page ke halaman terakhir yang valid
        setPage(data.page || targetPage);
      }
    } catch (error) {
      toast.error("Gagal memuat data");
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadCustomers(1);
  };

  const handlePageChange = (newPage) => {
    const maxPage = Math.max(Math.ceil(totalCount / PAGE_SIZE), 1);
    if (newPage < 1 || newPage > maxPage || newPage === page) return;
    setPage(newPage);
    setExpandedCustomer(null);
    loadCustomers(newPage);
  };

  const handleSubmit = async () => {
    if (!form.name) {
      toast.error("Nama wajib diisi");
      return;
    }
    try {
      if (editItem) {
        await api.put(`/customers/${editItem.id}`, form);
        toast.success("Customer berhasil diupdate");
      } else {
        await api.post("/customers", form);
        toast.success("Customer berhasil ditambahkan");
      }
      setShowModal(false);
      setEditItem(null);
      setForm({ name: "", phone: "", email: "" });
      loadCustomers();
    } catch (error) {
      const msg = error.response?.data?.message;
      toast.error(msg || "Gagal menyimpan");
    }
  };

  const handleEdit = (c) => {
    setEditItem(c);
    setForm({
      name: c.name,
      phone: c.phone || "",
      email: c.email || "",
    });
    setShowModal(true);
  };

  const openVehicleModal = (customer, vehicle = null) => {
    setSelectedCustomer(customer);
    setEditVehicle(vehicle);
    if (vehicle) {
      setVehicleForm({
        vehicle_type: vehicle.vehicle_type || "",
        plate_number: vehicle.plate_number || "",
        brand: vehicle.brand || "",
        color: vehicle.color || "",
      });
      const progressMap = {};
      (vehicle.loyalty_progress || []).forEach((lp) => {
        progressMap[lp.rule_id] = lp.current_count;
      });
      setLoyaltyProgressEdit(progressMap);
    } else {
      setVehicleForm({
        vehicle_type: "",
        plate_number: "",
        brand: "",
        color: "",
      });
      setLoyaltyProgressEdit({});
    }
    setShowVehicleModal(true);
  };

  const handleVehicleSubmit = async () => {
    if (!vehicleForm.vehicle_type) {
      toast.error("Jenis kendaraan wajib diisi");
      return;
    }
    const vt = vehicleTypes.find((t) => t.name === vehicleForm.vehicle_type);
    if (vt && vt.requires_plate && !vehicleForm.plate_number) {
      toast.error(`Plat nomor wajib diisi untuk ${vehicleForm.vehicle_type}`);
      return;
    }
    try {
      let vehicleId;
      if (editVehicle) {
        await api.put(
          `/customers/${selectedCustomer.id}/vehicles/${editVehicle.id}`,
          vehicleForm,
        );
        vehicleId = editVehicle.id;
        toast.success("Kendaraan berhasil diupdate");
      } else {
        const res = await api.post(
          `/customers/${selectedCustomer.id}/vehicles`,
          vehicleForm,
        );
        vehicleId = res.data?.id;
        toast.success("Kendaraan berhasil ditambahkan");
      }
      // Save loyalty progress if there are active rules
      const applicableRules = loyaltyRules.filter((rule) => {
        if (!rule.vehicle_types) return true;
        return rule.vehicle_types
          .split(",")
          .map((s) => s.trim())
          .includes(vehicleForm.vehicle_type);
      });
      if (vehicleId && applicableRules.length > 0) {
        const progress = applicableRules.map((rule) => ({
          loyalty_rule_id: rule.id,
          current_count: loyaltyProgressEdit[rule.id] ?? 0,
        }));
        await api.put(
          `/customers/${selectedCustomer.id}/vehicles/${vehicleId}/loyalty`,
          { progress },
        );
      }
      setShowVehicleModal(false);
      loadCustomers();
    } catch (error) {
      const msg = error.response?.data?.message;
      toast.error(msg || "Gagal menyimpan kendaraan");
    }
  };

  const handleDeleteCustomer = (id, name) => {
    openConfirm(
      "Hapus Customer",
      `Hapus customer "${name}"? Kendaraan terkait juga akan dihapus.`,
      async () => {
        closeConfirm();
        try {
          await api.delete(`/customers/${id}`);
          toast.success("Customer berhasil dihapus");
          loadCustomers();
        } catch (error) {
          toast.error("Gagal menghapus customer");
        }
      },
    );
  };

  const handleDeleteVehicle = (customerId, vehicleId) => {
    openConfirm("Hapus Kendaraan", "Hapus kendaraan ini?", async () => {
      closeConfirm();
      try {
        await api.delete(`/customers/${customerId}/vehicles/${vehicleId}`);
        toast.success("Kendaraan berhasil dihapus");
        loadCustomers();
      } catch (error) {
        toast.error("Gagal menghapus kendaraan");
      }
    });
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Manajemen Customer</h4>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditItem(null);
            setForm({ name: "", phone: "", email: "" });
            setShowModal(true);
          }}
        >
          + Tambah Customer
        </button>
      </div>

      <div className="card p-3 mb-3">
        <div className="row g-2">
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder="Cari nama, HP, atau plat..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <div className="col-md-2">
            <button className="btn btn-primary" onClick={handleSearch}>
              Cari
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Nama</th>
                <th>HP</th>
                <th>Kendaraan</th>
                <th className="text-end">Total Belanja</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <React.Fragment key={c.id}>
                  <tr>
                    <td className="fw-bold">{c.name}</td>
                    <td>{c.phone || "-"}</td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <span className="badge bg-secondary">
                          {(c.vehicles || []).length} kendaraan
                        </span>
                        {(c.vehicles || []).length > 0 && (
                          <button
                            className="btn btn-sm btn-link p-0 text-decoration-none"
                            onClick={() =>
                              setExpandedCustomer(
                                expandedCustomer === c.id ? null : c.id,
                              )
                            }
                          >
                            {expandedCustomer === c.id ? "▲" : "▼"}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="text-end">{fmt(c.total_spent)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEdit(c)}
                          title="Edit Customer"
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => openVehicleModal(c)}
                          title="Tambah Kendaraan"
                        >
                          <FiPlus size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteCustomer(c.id, c.name)}
                          title="Hapus Customer"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedCustomer === c.id &&
                    (c.vehicles || []).length > 0 && (
                      <tr>
                        <td colSpan="7" className="bg-light p-0">
                          <table className="table table-sm mb-0">
                            <thead>
                              <tr className="text-muted small">
                                <th style={{ paddingLeft: "2rem" }}>
                                  <FiTruck className="me-1" />
                                  Jenis
                                </th>
                                <th>Plat Nomor</th>
                                <th>Merk</th>
                                <th>Warna</th>
                                <th>Termasuk Transaksi</th>
                                <th className="text-center">
                                  Loyalty Progress
                                </th>
                                <th>Aksi</th>
                              </tr>
                            </thead>
                            <tbody>
                              {c.vehicles.map((v) => (
                                <tr key={v.id}>
                                  <td style={{ paddingLeft: "2rem" }}>
                                    {v.vehicle_type}
                                  </td>
                                  <td className="fw-bold">
                                    {v.plate_number || "-"}
                                  </td>
                                  <td>{v.brand || "-"}</td>
                                  <td>{v.color || "-"}</td>
                                  <td>
                                    {v.top_category_name ? (
                                      <span
                                        className="badge"
                                        style={{
                                          background: "#1a56db",
                                          color: "#fff",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {v.top_category_name}
                                      </span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    {(v.loyalty_progress || []).length > 0 ? (
                                      <div>
                                        {v.loyalty_progress.map((lp) => (
                                          <div
                                            key={lp.rule_id}
                                            className="small"
                                          >
                                            {lp.rule_name}:{" "}
                                            <strong>{lp.current_count}</strong>/
                                            {lp.required_count}
                                            {lp.current_count >=
                                              lp.required_count && (
                                              <Badge
                                                bg="success"
                                                className="ms-1"
                                                style={{ fontSize: "0.6rem" }}
                                              >
                                                REDEEM
                                              </Badge>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => openVehicleModal(c, v)}
                                      >
                                        <FiEdit2 size={12} />
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                          handleDeleteVehicle(c.id, v.id)
                                        }
                                      >
                                        <FiTrash2 size={12} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                </React.Fragment>
              ))}
              {customers.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    Belum ada data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        page={page}
        totalCount={totalCount}
        pageSize={PAGE_SIZE}
        onPageChange={handlePageChange}
        label="customer"
      />

      {/* Customer Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editItem ? "Edit Customer" : "Tambah Customer"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Nama *</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>No. HP</Form.Label>
            <Form.Control
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Vehicle Modal */}
      <Modal
        show={showVehicleModal}
        onHide={() => setShowVehicleModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editVehicle ? "Edit Kendaraan" : "Tambah Kendaraan"}
            {selectedCustomer && (
              <div className="small text-muted fw-normal">
                {selectedCustomer.name}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Jenis Kendaraan *</Form.Label>
            <Form.Select
              value={vehicleForm.vehicle_type}
              onChange={(e) =>
                setVehicleForm({ ...vehicleForm, vehicle_type: e.target.value })
              }
            >
              <option value="">-- Pilih --</option>
              {vehicleTypes.map((vt) => (
                <option key={vt.id} value={vt.name}>
                  {vt.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>
              Plat Nomor{" "}
              {(() => {
                const vt = vehicleTypes.find(
                  (t) => t.name === vehicleForm.vehicle_type,
                );
                return vt && vt.requires_plate ? "*" : "";
              })()}
            </Form.Label>
            <Form.Control
              value={vehicleForm.plate_number}
              onChange={(e) =>
                setVehicleForm({
                  ...vehicleForm,
                  plate_number: e.target.value.toUpperCase(),
                })
              }
              onBlur={(e) =>
                setVehicleForm({
                  ...vehicleForm,
                  plate_number: formatPlate(e.target.value),
                })
              }
              placeholder={(() => {
                const vt = vehicleTypes.find(
                  (t) => t.name === vehicleForm.vehicle_type,
                );
                return vt && !vt.requires_plate ? "Opsional" : "B 1234 ABC";
              })()}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Merk</Form.Label>
            <Form.Control
              value={vehicleForm.brand}
              onChange={(e) =>
                setVehicleForm({ ...vehicleForm, brand: e.target.value })
              }
              placeholder="Toyota Avanza, Honda Civic, dll"
            />
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Warna</Form.Label>
            <Form.Control
              value={vehicleForm.color}
              onChange={(e) =>
                setVehicleForm({ ...vehicleForm, color: e.target.value })
              }
              placeholder="Hitam, Putih, dll"
            />
          </Form.Group>

          {/* Loyalty Progress */}
          {loyaltyRules.filter((rule) => {
            if (!rule.vehicle_types) return true;
            return rule.vehicle_types
              .split(",")
              .map((s) => s.trim())
              .includes(vehicleForm.vehicle_type);
          }).length > 0 && (
            <div className="mt-3 border-top pt-3">
              <div className="small fw-semibold text-muted mb-2">
                Progress Loyalti
              </div>
              {loyaltyRules
                .filter((rule) => {
                  if (!rule.vehicle_types) return true;
                  return rule.vehicle_types
                    .split(",")
                    .map((s) => s.trim())
                    .includes(vehicleForm.vehicle_type);
                })
                .map((rule) => (
                  <Form.Group className="mb-2" key={rule.id}>
                    <Form.Label className="small">
                      {rule.name}{" "}
                      <span className="text-muted">
                        ({loyaltyProgressEdit[rule.id] ?? 0}/
                        {rule.required_count}x)
                      </span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      min={0}
                      max={rule.required_count}
                      value={loyaltyProgressEdit[rule.id] ?? 0}
                      onChange={(e) =>
                        setLoyaltyProgressEdit({
                          ...loyaltyProgressEdit,
                          [rule.id]: Math.max(
                            0,
                            Math.min(
                              rule.required_count,
                              Number(e.target.value),
                            ),
                          ),
                        })
                      }
                    />
                  </Form.Group>
                ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowVehicleModal(false)}
          >
            Batal
          </Button>
          <Button variant="primary" onClick={handleVehicleSubmit}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default Customers;
